import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { StudyPlan, PlanType } from '../models';
import { BASE_URL, createAuthorizationHeader } from '../../models/globals';

@Injectable({
  providedIn: 'root'
})
export class StudyPlanService {
  private currentPlanSubject = new BehaviorSubject<StudyPlan | null>(null);
  public currentPlan$ = this.currentPlanSubject.asObservable();
  private storageKey = 'current-study-plan';
  private apiUrl = BASE_URL;

  constructor(private http: HttpClient) {
    // Load saved plan from localStorage if available
    this.loadSavedPlan();
  }

  /**
   * Load saved plan from local storage (fallback)
   */
  private loadSavedPlan(): void {
    try {
      const savedPlan = localStorage.getItem(this.storageKey);
      if (savedPlan) {
        const plan = JSON.parse(savedPlan);
        
        // Parse dates
        if (plan.startDate) plan.startDate = new Date(plan.startDate);
        if (plan.endDate) plan.endDate = new Date(plan.endDate);
        if (plan.unavailableDates) {
          plan.unavailableDates = plan.unavailableDates.map((d: string) => new Date(d));
        }
        if (plan.createdAt) plan.createdAt = new Date(plan.createdAt);
        if (plan.updatedAt) plan.updatedAt = new Date(plan.updatedAt);
        
        this.currentPlanSubject.next(plan);
      }
    } catch (e) {
      console.error('Error loading saved plan:', e);
    }
  }

  /**
   * Save plan to local storage for offline use
   */
  private savePlanToStorage(plan: StudyPlan): void {
    localStorage.setItem(this.storageKey, JSON.stringify(plan));
    // Also save the plan ID as the active plan ID
    localStorage.setItem('activePlanId', plan.id);
  }

  /**
   * Transform plan from API format to frontend format
   */
  private transformPlan(plan: any): StudyPlan {
    console.log('Raw plan data from API:', plan);
    
    // Handle the case where the plan might have either snake_case or camelCase keys
    let unavailableDates = [];
    // Try both formats for unavailable dates
    if (plan.unavailable_dates) {
      try {
        unavailableDates = typeof plan.unavailable_dates === 'string' 
          ? JSON.parse(plan.unavailable_dates) 
          : plan.unavailable_dates;
      } catch (e) {
        console.error('Error parsing unavailable_dates:', e);
      }
    } else if (plan.unavailableDates) {
      unavailableDates = plan.unavailableDates;
    }
    
    // Make sure dates are Date objects
    unavailableDates = (unavailableDates || []).map((d: string | Date) => 
      typeof d === 'string' ? new Date(d) : d
    );
    
    // Try both formats for catch up days
    let catchUpDays = [];
    if (plan.catch_up_days) {
      try {
        catchUpDays = typeof plan.catch_up_days === 'string' 
          ? JSON.parse(plan.catch_up_days) 
          : plan.catch_up_days;
      } catch (e) {
        console.error('Error parsing catch_up_days:', e);
      }
    } else if (plan.catchUpDays) {
      catchUpDays = plan.catchUpDays;
    }
    
    // Fix: Try both formats for selected days
    let selectedDays = [];
    console.log('Looking for selectedDays in plan data:', plan);

    // Check all possible property names for selected days
    if (plan.selected_days) {
      try {
        selectedDays = typeof plan.selected_days === 'string' 
          ? JSON.parse(plan.selected_days) 
          : plan.selected_days;
        console.log('Found and parsed selected_days (snake_case):', selectedDays);
      } catch (e) {
        console.error('Error parsing selected_days:', e);
      }
    } else if (plan.selectedDays) {
      selectedDays = plan.selectedDays;
      console.log('Found selectedDays (camelCase):', selectedDays);
    } else if (plan.days) {
      selectedDays = plan.days;
      console.log('Found days property:', selectedDays);
    } else if (plan.weekdays) {
      selectedDays = plan.weekdays;
      console.log('Found weekdays property:', selectedDays);
    } else {
      console.warn('No selectedDays property found in any format. Raw plan:', plan);
      
      // Check if selectedDays might be nested in another property
      for (const key in plan) {
        if (plan[key] && typeof plan[key] === 'object') {
          if (plan[key].selectedDays || plan[key].selected_days) {
            selectedDays = plan[key].selectedDays || plan[key].selected_days;
            console.log(`Found selectedDays in nested property ${key}:`, selectedDays);
            break;
          }
        }
      }
    }
    
    const userId = plan.user_id || plan.userId;
    const planType = plan.plan_type || plan.planType;
    const startDate = plan.start_date ? new Date(plan.start_date) : 
                    (plan.startDate ? new Date(plan.startDate) : new Date());
    const endDate = plan.end_date ? new Date(plan.end_date) : 
                  (plan.endDate ? new Date(plan.endDate) : new Date());
    const minutesPerQuestion = plan.minutes_per_question || plan.minutesPerQuestion || 6;
    const flashcardsPerMinute = plan.flashcards_per_minute || plan.flashcardsPerMinute || 5;
    const videosPerDay = plan.videos_per_day || plan.videosPerDay || 52;
    const includesPracticeQuestions = plan.includes_practice_questions !== undefined 
      ? !!plan.includes_practice_questions 
      : (plan.includesPracticeQuestions !== undefined ? !!plan.includesPracticeQuestions : true);
    const includesFlashcards = plan.includes_flashcards !== undefined 
      ? !!plan.includes_flashcards 
      : (plan.includesFlashcards !== undefined ? !!plan.includesFlashcards : true);
    const includesCourseVideos = plan.includes_course_videos !== undefined 
      ? !!plan.includes_course_videos 
      : (plan.includesCourseVideos !== undefined ? !!plan.includesCourseVideos : false);
    const createdAt = plan.created_at ? new Date(plan.created_at) : 
                    (plan.createdAt ? new Date(plan.createdAt) : new Date());
    const updatedAt = plan.updated_at ? new Date(plan.updated_at) : 
                    (plan.updatedAt ? new Date(plan.updatedAt) : new Date());
    
    // Create the transformed plan with all data
    const transformedPlan = {
      id: (plan.id || '0').toString(),
      userId,
      planType,
      startDate,
      endDate,
      minutesPerQuestion,
      flashcardsPerMinute,
      videosPerDay,
      unavailableDates,
      catchUpDays,
      selectedDays,
      includesPracticeQuestions,
      includesFlashcards,
      includesCourseVideos,
      createdAt,
      updatedAt
    };
    
    console.log('Transformed plan:', transformedPlan);
    return transformedPlan as StudyPlan;
  }

  /**
   * Get current study plan
   */
  getCurrentPlan(): StudyPlan | null {
    return this.currentPlanSubject.value;
  }

  /**
   * Load the active study plan for the current user
   */
  loadActivePlan(courseId: any): Observable<StudyPlan | null> {
    return this.http.get<any>(`${this.apiUrl}/study-plans/active?courseId=${courseId}`, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        if (response.success && response.data) {
          const plan = this.transformPlan(response.data);
          this.currentPlanSubject.next(plan);
          this.savePlanToStorage(plan);
          return plan;
        }
        return null;
      }),
      catchError(error => {
        console.error('Error loading active plan:', error);
        return of(null);
      })
    );
  }

  /**
   * Get all user study plans
   */
  getUserPlans(): Observable<StudyPlan[]> {
    return this.http.get<any>(`${this.apiUrl}/study-plans`, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data.map((plan: any) => this.transformPlan(plan));
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching user plans:', error);
        return of([]);
      })
    );
  }

  /**
   * Get a plan by ID
   */
  getPlanById(planId: string): Observable<StudyPlan | null> {
    return this.http.get<any>(`${this.apiUrl}/study-plans/${planId}`, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        if (response.success && response.data) {
          return this.transformPlan(response.data);
        }
        return null;
      }),
      catchError(error => {
        console.error('Error fetching plan by ID:', error);
        return of(null);
      })
    );
  }

  /**
   * Create a new study plan
   */
  createPlan(planData: Partial<StudyPlan>): Observable<StudyPlan> {
    console.log('Creating plan with data:', JSON.stringify(planData, null, 2));
    
    // Log specifically the selectedDays for debugging
    if (planData.selectedDays) {
      console.log('selectedDays in create payload:', JSON.stringify(planData.selectedDays, null, 2));
    } else {
      console.warn('No selectedDays in create payload!');
    }
    
    return this.http.post<any>(`${this.apiUrl}/study-plans`, planData, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        if (response.success && response.data) {
          const plan = this.transformPlan(response.data);
          this.currentPlanSubject.next(plan);
          this.savePlanToStorage(plan);
          return plan;
        }
        throw new Error('Failed to create plan');
      }),
      catchError(error => {
        console.error('Error creating plan:', error);
        return throwError(() => new Error('Failed to create plan: ' + (error.message || 'Unknown error')));
      })
    );
  }

  /**
   * Create a MaxFlex plan
   */
  createMaxFlexPlan(planData: Partial<StudyPlan>): Observable<StudyPlan> {
    // Ensure planType is set to MaxFlex
    const maxFlexPlanData = {
      ...planData,
      planType: PlanType.MAX_FLEX
    };
    
    return this.createPlan(maxFlexPlanData);
  }
  
  /**
   * Create a Select Study plan
   */
  createSelectStudyPlan(planData: Partial<StudyPlan>): Observable<StudyPlan> {
    // Ensure planType is set to SelectStudy
    const selectStudyPlanData = {
      ...planData,
      planType: PlanType.SELECT_STUDY
    };
    
    return this.createPlan(selectStudyPlanData);
  }

  /**
   * Update an existing plan
   */
  updatePlan(planId: string, updates: Partial<StudyPlan>): Observable<StudyPlan> {
    const endpoint = `${this.apiUrl}/study-plans/${planId}`;
    console.log(`Updating plan at: ${endpoint}`);
    console.log('Update payload:', JSON.stringify(updates, null, 2));
    
    // Log specifically the selectedDays for debugging
    if (updates.selectedDays) {
      console.log('selectedDays in update payload:', JSON.stringify(updates.selectedDays, null, 2));
    } else {
      console.warn('No selectedDays in update payload!');
    }
    
    return this.http.put<any>(endpoint, updates, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        if (response.success && response.data) {
          const plan = this.transformPlan(response.data);
          
          // Update current plan if this is the active one
          const currentPlan = this.currentPlanSubject.value;
          if (currentPlan && currentPlan.id === planId) {
            this.currentPlanSubject.next(plan);
            this.savePlanToStorage(plan);
          }
          
          return plan;
        }
        throw new Error('Failed to update plan');
      }),
      catchError(error => {
        console.error(`Error updating plan at ${endpoint}:`, error);
        
        // Fallback to local implementation if API fails
        console.log("API update failed. Using local update fallback.");
        const currentPlan = this.currentPlanSubject.value;
        if (currentPlan && currentPlan.id === planId) {
          const updatedPlan: StudyPlan = {
            ...currentPlan,
            ...updates,
            updatedAt: new Date()
          };
          this.currentPlanSubject.next(updatedPlan);
          this.savePlanToStorage(updatedPlan);
          
          console.log("Plan updated locally:", updatedPlan);
          return of(updatedPlan);
        }
        return throwError(() => new Error('Plan not found'));
      })
    );
  }

  /**
   * Set a plan as active
   */
  setActivePlan(planId: string): Observable<boolean> {
    return this.http.put<any>(`${this.apiUrl}/study-plans/${planId}/activate`, {}, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        if (response.success) {
          // Load the activated plan into current state
          this.getPlanById(planId).subscribe(plan => {
            if (plan) {
              this.currentPlanSubject.next(plan);
              this.savePlanToStorage(plan);
            }
          });
          return true;
        }
        return false;
      }),
      catchError(error => {
        console.error('Error setting active plan:', error);
        return of(false);
      })
    );
  }

  /**
   * Delete a study plan
   */
  deletePlan(planId: string): Observable<boolean> {
    return this.http.delete<any>(`${this.apiUrl}/study-plans/${planId}`, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        if (response.success) {
          // If deleting current plan, clear current plan
          const currentPlan = this.currentPlanSubject.value;
          if (currentPlan && currentPlan.id === planId) {
            this.currentPlanSubject.next(null);
            localStorage.removeItem(this.storageKey);
            localStorage.removeItem('activePlanId');
          }
          return true;
        }
        return false;
      }),
      catchError(error => {
        console.error('Error deleting plan:', error);
        return of(false);
      })
    );
  }

  /**
   * Get course questions
   */
  getCourseQuestions(courseId: any, userId: any): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/study-plans/getCourseQuestions`, { courseId, userId }, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error('Error returning course questions:', error);
        return of(false);
      })
    );
  }

  /**
   * Calculate total hours based on plan settings
   */
  calculateTotalHours(includesPracticeQuestions: boolean, includesFlashcards: boolean, includesCourseVideos: boolean, totalQuestions: number, totalFlashcards: number, selectedCourse: number): number {
    let totalHours = 0;
    
    if (includesPracticeQuestions) {
      totalHours += parseInt(((totalQuestions * 5) / 60).toFixed(0));
    }
    
    if (includesFlashcards) {
      totalHours += parseInt(((totalFlashcards / 5) / 60).toFixed(0));
    }

    return totalHours;
  }

  /**
   * Calculate catch-up days based on frequency and weekday
   */
  calculateCatchUpDays(startDate: Date, endDate: Date, frequency: string, weekday: number): Date[] {
    const catchUpDays: Date[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Adjust start date to the first occurrence of the weekday
    while (start.getDay() !== weekday) {
      start.setDate(start.getDate() + 1);
    }
    
    // Add catch-up days based on frequency
    const current = new Date(start);
    while (current <= end) {
      catchUpDays.push(new Date(current));
      
      // Increment based on frequency
      switch (frequency) {
        case 'every':
          current.setDate(current.getDate() + 7);
          break;
        case 'everyOther':
          current.setDate(current.getDate() + 14);
          break;
        case 'everyThird':
          current.setDate(current.getDate() + 21);
          break;
        case 'everyFourth':
          current.setDate(current.getDate() + 28);
          break;
      }
    }
    
    return catchUpDays;
  }

  getCourseSchedule(courseId: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/getUserCourseSchedule`, { courseId }, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error('Error returning course schedule:', error);
        return of(false);
      })
    );
  }

  getCourseVideoContent(videoLink: any, userId: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/getCourseVideoContent`, { videoLink, userId }, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error('Error returning course video content:', error);
        return of(false);
      })
    );
  }

  /**
   * Get tasks statistics for a course and user with optional date filtering
   * @param courseId Course ID
   * @param userId User ID
   * @param startDate Optional start date for filtering (YYYY-MM-DD format)
   * @param endDate Optional end date for filtering (YYYY-MM-DD format)
   */
  getTasksStats(courseId: any, userId: any, startDate?: string, endDate?: string): Observable<any> {
    let params = `courseId=${courseId}&userId=${userId}`;
    
    // Add date parameters if provided
    if (startDate && endDate) {
      params += `&startDate=${startDate}&endDate=${endDate}`;
    }
    
    return this.http.get<any>(`${this.apiUrl}/study-plans/tasks/stats?${params}`, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error('Error returning tasks stats:', error);
        return of(null);
      })
    );
  }

  /**
   * Update a task's completion status
   * @param planId Plan ID
   * @param taskId Task ID
   * @param updateData Data to update (e.g., { isCompleted: true })
   */
  updateTask(planId: string, taskId: string, updateData: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/study-plans/${planId}/tasks/${taskId}`, updateData, {headers: createAuthorizationHeader()}).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error('Error updating task:', error);
        return throwError(() => error);
      })
    );
  }
} 